import ky from 'ky';

import { ApiError } from './error';
import { readCsrfToken } from './csrf';
import { readEnv } from '../utils/env';

// allow to set the auth header from the environment variable, useful fo OAuth
const AuthHeader = readEnv('VITE_AUTH_HEADER', '');
const BaseUrl = readEnv('VITE_API_URL', '');
export const apiBaseUrl = BaseUrl.replace(/\/$/, '');

/**
 * A wrapper around the ky library that sets the credentials to include
 * and sets the authorization header if it is set in the environment
 * variables.
 * Provides a more convenient way to make HTTP requests to the backend
 * without having to deal with authentication, fetch, etc.
 */
export const api = ky.create({
  prefixUrl: apiBaseUrl,
  credentials: 'include',
  timeout: 30_000,
  hooks: {
    beforeRequest: [
      (req) => {
        if (AuthHeader) req.headers.set('Authorization', AuthHeader);
        const method = (req.method || 'GET').toUpperCase();
        if (!['GET', 'HEAD', 'OPTIONS', 'TRACE'].includes(method)) {
          const csrf = readCsrfToken();
          if (csrf) {
            req.headers.set('x-csrf-token', csrf);
          }
        }
      },
    ],
    afterResponse: [
      async (req, _opts, res) => {
        // console.debug('[response]', res.status, req.method, req.url, Object.fromEntries(res.headers));
        if (!res.ok) {
          const data = await parseErrorPayload(res);
          const rawError = typeof data?.error === 'string' ? data.error.trim() : '';
          const [codePart, ...messageSegments] = rawError.split(':');
          const normalizedCode =
            codePart && codePart.trim().length > 0 ? codePart.trim() : `http_${res.status}`;
          const messageFromError = messageSegments.join(':').trim();
          const retryIn = typeof data?.retry_in === 'number' ? data.retry_in : null;
          const message =
            resolveErrorMessage(normalizedCode, res.status, data, messageFromError) ??
            `Request failed with status ${res.status}`;
          const details =
            data && typeof data === 'object' && 'details' in data ? (data as any).details : data;
          throw new ApiError({
            status: res.status,
            code: normalizedCode,
            message,
            details,
            retryIn,
          });
        }
      },
    ],
  },
});

type ErrorPayload = {
  error?: unknown;
  message?: unknown;
  details?: unknown;
  retry_in?: unknown;
  [key: string]: unknown;
} | null;

const parseErrorPayload = async (res: Response): Promise<ErrorPayload> => {
  try {
    const json = await res.clone().json();
    if (json && typeof json === 'object') {
      return json as ErrorPayload;
    }
    return { details: json };
  } catch {
    try {
      const text = await res.clone().text();
      return text ? { message: text } : null;
    } catch {
      return null;
    }
  }
};

const resolveErrorMessage = (
  code: string,
  status: number,
  payload: ErrorPayload,
  messageFromError: string,
): string | null => {
  const fromPayload = extractMessageFromPayload(payload);
  if (fromPayload) {
    return fromPayload;
  }
  if (messageFromError) {
    return messageFromError;
  }
  const normalized = code.toLowerCase();
  const defaults: Record<string, string> = {
    rate_limited: 'Too many requests. Please wait before trying again.',
    invalid_request: 'The request could not be processed. Check the inputs and try again.',
    action_not_found: 'The requested action could not be found.',
    action_forbidden: 'You do not have permission to perform this action.',
    action_conflict: 'The action is no longer in a state that allows this change.',
    invalid_action_payload:
      'The action payload is invalid. Please review the fields and try again.',
    task_not_found: 'The related task could not be found.',
    task_action_invalid: 'The task action could not be processed. Please review and try again.',
  };
  if (defaults[normalized]) {
    return defaults[normalized];
  }
  if (status === 401) {
    return 'You must be signed in to continue.';
  }
  if (status === 403) {
    return 'You do not have permission to perform this action.';
  }
  if (status === 404) {
    return 'The requested resource could not be found.';
  }
  if (status >= 500) {
    return 'The server is currently unavailable. Please try again soon.';
  }
  return null;
};

const extractMessageFromPayload = (payload: ErrorPayload): string | null => {
  if (!payload) {
    return null;
  }
  const messageCandidates: unknown[] = [];
  if (typeof payload.message === 'string') {
    messageCandidates.push(payload.message);
  }
  const details = payload.details as unknown;
  if (typeof details === 'string') {
    messageCandidates.push(details);
  } else if (details && typeof details === 'object') {
    const nested = (details as { message?: unknown }).message;
    if (typeof nested === 'string') {
      messageCandidates.push(nested);
    }
  }
  const errorField = payload.error;
  if (typeof errorField === 'string') {
    const [, ...segments] = errorField.split(':');
    if (segments.length > 0) {
      messageCandidates.push(segments.join(':').trim());
    } else if (!errorField.startsWith('http_')) {
      messageCandidates.push(errorField.replace(/_/g, ' '));
    }
  }
  const candidate = messageCandidates.find(
    (value) => typeof value === 'string' && value.trim().length > 0,
  );
  return typeof candidate === 'string' ? candidate.trim() : null;
};
